import { useState } from 'react'
import styled from 'styled-components'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import useApi from '../../../hooks/useApi'
import useTranslation from '../../../hooks/useTranslation'

const PageWrap = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
`

const Header = styled.div`
    padding: 1rem 1.5rem 0.75rem;
    border-bottom: 1px solid ${p => p.theme.form.border};
    flex-shrink: 0;
`

const Title = styled.h2`
    font-size: 1.1rem;
    font-weight: 600;
    color: ${p => p.theme.font.color};
    margin: 0;
`

const Body = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 1rem 1.5rem;
`

const Section = styled.section`
    margin-bottom: 2rem;
`

const SectionTitle = styled.h3`
    font-size: 0.95rem;
    font-weight: 600;
    color: ${p => p.theme.font.color};
    margin: 0 0 0.5rem;
`

const Note = styled.p`
    font-size: 0.85rem;
    color: ${p => p.theme.font.label};
    margin: 0 0 0.75rem;
`

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
`

const Th = styled.th`
    text-align: left;
    padding: 0.5rem 0.75rem;
    color: ${p => p.theme.font.label};
    font-weight: 600;
    border-bottom: 1px solid ${p => p.theme.form.border};
`

const Td = styled.td`
    padding: 0.4rem 0.75rem;
    color: ${p => p.theme.font.color};
    border-bottom: 1px solid ${p => p.theme.form.border};
    vertical-align: top;
`

const Problem = styled.span`
    color: ${p => p.theme.font.label};
    font-size: 0.8rem;
`

const Action = styled.button`
    background: ${p => p.theme.btn.primary};
    border: none;
    border-radius: 4px;
    color: #fff;
    cursor: pointer;
    font-size: 0.8rem;
    padding: 0.3rem 0.7rem;

    &:disabled {
        cursor: default;
        opacity: 0.5;
    }
`

/*
 * The recovery surface for posting (36.03 §9.6).
 *
 * Two questions, both of which the per-entity lists cannot answer because they
 * join documents to journals: which booked documents are not in the ledger, and
 * does every book still balance. Both should normally be empty and "yes" — the
 * reconciliation sweep works the same queue on a schedule, so anything sitting
 * here is something the sweep could not fix on its own.
 */
const PostingStatusPage = () => {
    const { t } = useTranslation()
    const { getMe, postMe } = useApi()
    const queryClient = useQueryClient()
    const [busy, setBusy] = useState(null)

    const { data: status, isLoading } = useQuery({
        queryKey: ['ledger', 'postingStatus'],
        queryFn: () => getMe('ledger/postingStatus')
    })

    const { data: balance } = useQuery({
        queryKey: ['ledger', 'trialBalance'],
        queryFn: () => getMe('ledger/trialBalance')
    })

    const rows = Array.isArray(status?.data) ? status.data : []
    const dlq = Array.isArray(status?.dlq) ? status.dlq : []
    const books = Array.isArray(balance?.data) ? balance.data : []

    const repost = async (row) => {
        setBusy(row.id)
        try {
            await postMe(`${row.entityKey}/${row.id}/repost`, {})
            await queryClient.invalidateQueries({ queryKey: ['ledger'] })
        } finally {
            setBusy(null)
        }
    }

    // A row is either "never posted" or "posted, but the document does not say
    // so" — the second one needs no posting, only the stamp put right.
    const problem = (row) => row.journalId
        ? t('accounting.postingStatus.problem.notStamped')
        : row.error
            ? `${t('accounting.postingStatus.problem.failed')} — ${t(row.error)}`
            : t('accounting.postingStatus.problem.missing')

    return (
        <PageWrap>
            <Header>
                <Title>{t('accounting.postingStatus.title')}</Title>
            </Header>
            <Body>
                <Section>
                    <SectionTitle>{t('accounting.postingStatus.gaps.title')}</SectionTitle>
                    <Note>{t('accounting.postingStatus.gaps.note')}</Note>
                    {isLoading ? (
                        <div>…</div>
                    ) : rows.length === 0 ? (
                        <div>{t('accounting.postingStatus.gaps.empty')}</div>
                    ) : (
                        <Table>
                            <thead>
                                <tr>
                                    <Th>{t('accounting.postingStatus.table.document')}</Th>
                                    <Th>{t('accounting.postingStatus.table.date')}</Th>
                                    <Th>{t('accounting.postingStatus.table.customer')}</Th>
                                    <Th style={{ textAlign: 'right' }}>{t('accounting.postingStatus.table.amount')}</Th>
                                    <Th>{t('accounting.postingStatus.table.problem')}</Th>
                                    <Th />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(row => (
                                    <tr key={`${row.entityKey}-${row.id}`}>
                                        <Td>{row.number}</Td>
                                        <Td>{row.date ? String(row.date).slice(0, 10) : ''}</Td>
                                        <Td>{row.customerName}</Td>
                                        <Td style={{ textAlign: 'right' }}>
                                            {row.amount == null ? '' : `${Number(row.amount).toFixed(2)} ${row.currency ?? ''}`}
                                        </Td>
                                        <Td><Problem>{problem(row)}</Problem></Td>
                                        <Td style={{ textAlign: 'right' }}>
                                            <Action disabled={busy === row.id} onClick={() => repost(row)}>
                                                {t('accounting.postingStatus.action.repost')}
                                            </Action>
                                        </Td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Section>

                {dlq.length > 0 && (
                    <Section>
                        <SectionTitle>{t('accounting.postingStatus.dlq.title')}</SectionTitle>
                        <Note>{t('accounting.postingStatus.dlq.note')}</Note>
                        <Table>
                            <thead>
                                <tr>
                                    <Th>{t('accounting.postingStatus.table.event')}</Th>
                                    <Th>{t('accounting.postingStatus.table.document')}</Th>
                                    <Th style={{ textAlign: 'right' }}>{t('accounting.postingStatus.table.retries')}</Th>
                                    <Th>{t('accounting.postingStatus.table.problem')}</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {dlq.map(row => (
                                    <tr key={row.id}>
                                        <Td>{row.routingKey}</Td>
                                        <Td>{row.number || row.documentId}</Td>
                                        <Td style={{ textAlign: 'right' }}>{row.retries}</Td>
                                        <Td><Problem>{row.error}</Problem></Td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Section>
                )}

                <Section>
                    <SectionTitle>{t('accounting.postingStatus.balance.title')}</SectionTitle>
                    <Note>{t('accounting.postingStatus.balance.note')}</Note>
                    {books.length === 0 ? (
                        <div>{t('accounting.postingStatus.balance.empty')}</div>
                    ) : (
                        <Table>
                            <thead>
                                <tr>
                                    <Th>{t('accounting.postingStatus.table.book')}</Th>
                                    <Th style={{ textAlign: 'right' }}>{t('accounting.postingStatus.table.entries')}</Th>
                                    <Th style={{ textAlign: 'right' }}>{t('accounting.postingStatus.table.sum')}</Th>
                                    <Th>{t('accounting.postingStatus.table.verdict')}</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {books.map(book => (
                                    <tr key={book.bookId}>
                                        <Td>{book.bookId}</Td>
                                        <Td style={{ textAlign: 'right' }}>{book.rows}</Td>
                                        <Td style={{ textAlign: 'right' }}>{Number(book.total).toFixed(2)}</Td>
                                        <Td>
                                            {book.balanced
                                                ? t('accounting.postingStatus.balance.ok')
                                                : t('accounting.postingStatus.balance.broken')}
                                        </Td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Section>
            </Body>
        </PageWrap>
    )
}

export default PostingStatusPage
